import React, { useState, useEffect } from 'react';
import { useParams, Link, useSearchParams } from 'react-router-dom';
import { 
  Award, ArrowLeft, Download, Share2, CheckCircle, Trophy,
  Calendar, Users, Linkedin, Copy, ExternalLink, Shield, Loader,
  QrCode, Eye, TrendingUp, FileDown, BadgeCheck, XCircle
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import ShareButton from '../components/ShareButton';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Certificate View/Verify Page
const CodeArenaCertificatePage = () => {
  const { certId } = useParams();
  const [searchParams] = useSearchParams();
  const [certificate, setCertificate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [verificationCode, setVerificationCode] = useState(searchParams.get('code') || '');
  const [verifyResult, setVerifyResult] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [downloading, setDownloading] = useState(false);

  const token = localStorage.getItem('codearena_token');

  useEffect(() => {
    if (certId) {
      fetchCertificate();
    } else {
      setLoading(false);
      if (searchParams.get('code')) {
        verifyCertificate(searchParams.get('code'));
      }
    }
  }, [certId, searchParams]);

  const fetchCertificate = async () => {
    try {
      const res = await axios.get(`${API}/codearena/certificates/${certId}`);
      setCertificate(res.data);
      if (token) {
        try {
          const analyticsRes = await axios.get(`${API}/codearena/certificates/${certId}/analytics`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setAnalytics(analyticsRes.data);
        } catch (err) {}
      }
    } catch (err) {
      console.error('Failed to fetch certificate:', err);
    } finally {
      setLoading(false);
    }
  };

  const verifyCertificate = async (code = null) => {
    const codeToVerify = code || verificationCode.trim();
    if (!codeToVerify) {
      toast.error('Please enter a verification code');
      return;
    }
    try {
      const res = await axios.get(`${API}/codearena/certificates/verify/${codeToVerify}`);
      setVerifyResult(res.data);
      if (!res.data.valid) {
        toast.error('Certificate not found');
      }
    } catch (err) {
      toast.error('Verification failed');
    }
  };

  const downloadCertificate = async () => {
    setDownloading(true);
    try {
      const response = await axios.get(`${API}/codearena/certificates/${certId}/download`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `EvalentX_Arena_Certificate_${certId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Certificate downloaded!');
    } catch (err) {
      console.error('Download failed:', err);
      toast.error('Failed to download certificate');
    } finally {
      setDownloading(false);
    }
  };

  const shareToLinkedIn = async () => {
    try {
      await axios.post(`${API}/codearena/certificates/${certId}/track-share?platform=linkedin`);
    } catch (err) {}
    
    const text = certificate 
      ? `I earned a certificate for ${certificate.contest_title} on EvalentX Arena! Rank: #${certificate.rank}`
      : '';
    const url = `https://evalentx.com/codearena/certificate/${certId}`;
    window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}&summary=${encodeURIComponent(text)}`, '_blank');
  };

  const addToLinkedIn = async () => {
    try {
      const res = await axios.get(`${API}/codearena/certificates/${certId}/badge`);
      window.open(res.data.linkedin_add_url, '_blank');
      toast.success('Opening LinkedIn to add credential');
    } catch (err) {
      toast.error('Failed to get LinkedIn credential data');
    }
  };

  const copyCertificateLink = () => {
    navigator.clipboard.writeText(`https://evalentx.com/codearena/certificate/${certId}`);
    toast.success('Certificate link copied!');
  };

  const getRankBadge = (rank) => {
    if (rank === 1) return { color: '#915907', text: '1st Place', icon: '🥇' };
    if (rank === 2) return { color: '#666666', text: '2nd Place', icon: '🥈' };
    if (rank === 3) return { color: '#b24020', text: '3rd Place', icon: '🥉' };
    return { color: '#0a66c2', text: `Rank #${rank}`, icon: '🏅' };
  };

  // Verification Mode (no certId)
  if (!certId) {
    return (
      <div className="min-h-screen bg-[#f3f2ef]">
        <header className="sticky top-0 z-50 bg-white border-b border-gray-200 shadow-sm">
          <div className="max-w-2xl mx-auto px-6 py-4">
            <Link to="/codearena" className="flex items-center gap-2 text-[#666666] hover:text-[#191919] transition-colors text-sm">
              <ArrowLeft size={16} />
              Back to EvalentX Arena
            </Link>
          </div>
        </header>

        <div className="max-w-2xl mx-auto px-6 py-12">
          <div className="bg-white border border-gray-200 rounded-xl p-8 text-center shadow-sm">
            <div className="w-16 h-16 bg-[#0a66c2]/10 rounded-full flex items-center justify-center mx-auto mb-6">
              <Shield className="text-[#0a66c2]" size={32} />
            </div>
            <h1 className="text-2xl font-semibold text-[#191919] mb-2">Verify Certificate</h1>
            <p className="text-[#666666] mb-8 text-sm">Enter the verification code to validate a EvalentX certificate</p>

            <div className="flex gap-3 max-w-md mx-auto">
              <input
                type="text"
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value.toUpperCase())}
                placeholder="CV-XXXX-XXXX"
                className="flex-1 px-4 py-3 bg-white border border-gray-300 rounded-lg text-[#191919] text-center font-mono tracking-wider placeholder-[#666666] focus:outline-none focus:ring-2 focus:ring-[#0a66c2]/20 focus:border-[#0a66c2]"
                data-testid="verification-input"
              />
              <button
                onClick={() => verifyCertificate()}
                className="px-6 py-3 bg-[#0a66c2] text-white rounded-full font-medium hover:bg-[#004182] transition-colors"
                data-testid="verify-btn"
              >
                Verify
              </button>
            </div>

            {verifyResult && (
              <div className={`mt-8 p-6 rounded-xl border ${
                verifyResult.valid 
                  ? 'bg-[#057642]/5 border-[#057642]/30' 
                  : 'bg-[#b24020]/5 border-[#b24020]/30'
              }`}>
                {verifyResult.valid ? (
                  <div>
                    <CheckCircle className="mx-auto text-[#057642] mb-4" size={48} />
                    <h3 className="text-xl font-semibold text-[#057642] mb-2">Valid Certificate</h3>
                    <p className="text-[#191919] font-medium">{verifyResult.student_name}</p>
                    <p className="text-[#666666] text-sm">{verifyResult.college}</p>
                    <p className="text-[#666666] text-sm mt-2">{verifyResult.certificate?.contest_title}</p>
                    <p className="text-[#0a66c2] font-semibold">Rank #{verifyResult.certificate?.rank}</p>
                    <Link 
                      to={`/codearena/certificate/${verifyResult.certificate?.id}`}
                      className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-[#0a66c2] text-white rounded-full hover:bg-[#004182] transition-colors text-sm font-medium"
                      data-testid="view-cert-btn"
                    >
                      <Eye size={16} />
                      View Full Certificate
                    </Link>
                  </div>
                ) : (
                  <div>
                    <XCircle className="mx-auto text-[#b24020] mb-4" size={48} />
                    <p className="text-[#b24020]">Certificate not found. Please check the code and try again.</p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Certificate Display
  if (loading) {
    return (
      <div className="min-h-screen bg-[#f3f2ef] flex items-center justify-center">
        <Loader className="animate-spin text-[#0a66c2]" size={40} />
      </div>
    );
  }

  if (!certificate) {
    return (
      <div className="min-h-screen bg-[#f3f2ef] flex items-center justify-center">
        <div className="text-center bg-white p-8 rounded-xl border border-gray-200 shadow-sm">
          <Award className="mx-auto text-gray-300 mb-4" size={64} />
          <h2 className="text-2xl font-semibold text-[#191919] mb-2">Certificate Not Found</h2>
          <Link to="/codearena" className="text-[#0a66c2] hover:underline font-medium">
            Back to EvalentX Arena
          </Link>
        </div>
      </div>
    );
  }

  const rankBadge = getRankBadge(certificate.rank);

  return (
    <div className="min-h-screen bg-[#f3f2ef] py-8">
      <div className="max-w-4xl mx-auto px-6">
        <div className="flex items-center justify-between mb-6">
          <Link to="/codearena" className="flex items-center gap-2 text-[#666666] hover:text-[#191919] transition-colors text-sm">
            <ArrowLeft size={16} />
            Back to EvalentX Arena
          </Link>
          <div className="flex gap-2">
            <button
              onClick={downloadCertificate}
              disabled={downloading}
              className="flex items-center gap-1.5 px-4 py-2 bg-[#057642]/10 text-[#057642] rounded-lg hover:bg-[#057642]/20 transition-colors disabled:opacity-50 text-sm font-medium"
              title="Download PDF"
              data-testid="download-cert-btn"
            >
              {downloading ? <Loader size={16} className="animate-spin" /> : <FileDown size={16} />}
              {downloading ? 'Downloading...' : 'Download PDF'}
            </button>
            <button
              onClick={copyCertificateLink}
              className="p-2 bg-[#f3f2ef] rounded-lg hover:bg-gray-200 transition-colors border border-gray-200"
              title="Copy Link"
              data-testid="copy-link-btn"
            >
              <Copy size={16} className="text-[#666666]" />
            </button>
            <button
              onClick={addToLinkedIn}
              className="flex items-center gap-1.5 px-4 py-2 bg-[#0a66c2]/10 text-[#0a66c2] rounded-lg hover:bg-[#0a66c2]/20 transition-colors text-sm font-medium"
              title="Add to LinkedIn"
              data-testid="linkedin-add-btn"
            >
              <BadgeCheck size={16} />
              Add to LinkedIn
            </button>
            <button
              onClick={shareToLinkedIn}
              className="flex items-center gap-1.5 px-4 py-2 bg-[#0a66c2]/10 text-[#0a66c2] rounded-lg hover:bg-[#0a66c2]/20 transition-colors text-sm font-medium"
              title="Share on LinkedIn"
              data-testid="linkedin-share-btn"
            >
              <Linkedin size={16} />
              Share
            </button>
            <ShareButton kind="certificate" id={certId} label="Share Link" variant="solid" size="md" testid="share-certificate-btn" />
          </div>
        </div>

        {/* Analytics Section (Owner Only) */}
        {analytics && (
          <div className="mb-6 bg-white border border-gray-200 rounded-xl p-4 shadow-sm">
            <div className="flex items-center gap-2 mb-3">
              <TrendingUp size={16} className="text-[#0a66c2]" />
              <h3 className="text-[#191919] font-semibold text-sm">Certificate Analytics</h3>
            </div>
            <div className="grid grid-cols-4 gap-4">
              <div className="text-center p-3 bg-[#f3f2ef] rounded-lg">
                <p className="text-xl font-bold text-[#191919]">{analytics.downloads || 0}</p>
                <p className="text-xs text-[#666666]">Downloads</p>
              </div>
              <div className="text-center p-3 bg-[#f3f2ef] rounded-lg">
                <p className="text-xl font-bold text-[#191919]">{analytics.views || 0}</p>
                <p className="text-xs text-[#666666]">Views</p>
              </div>
              <div className="text-center p-3 bg-[#f3f2ef] rounded-lg">
                <p className="text-xl font-bold text-[#191919]">{analytics.verifications || 0}</p>
                <p className="text-xs text-[#666666]">Verifications</p>
              </div>
              <div className="text-center p-3 bg-[#f3f2ef] rounded-lg">
                <p className="text-xl font-bold text-[#191919]">{analytics.linkedin_shares || 0}</p>
                <p className="text-xs text-[#666666]">LinkedIn Shares</p>
              </div>
            </div>
          </div>
        )}

        {/* Certificate Card */}
        <div className="bg-white border-2 border-[#915907]/30 rounded-2xl overflow-hidden shadow-lg">
          {/* Header */}
          <div className="bg-gradient-to-r from-[#0a66c2] to-[#004182] px-8 py-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 bg-white rounded-xl flex items-center justify-center font-bold text-xl text-[#0a66c2]">
                  CV
                </div>
                <div>
                  <h1 className="text-xl font-bold text-white">EvalentX</h1>
                  <p className="text-white/80 text-sm">CodeArena Certificate</p>
                </div>
              </div>
              <div className="text-right">
                <div className="flex items-center gap-1.5 text-white">
                  <CheckCircle size={16} />
                  <span className="font-medium text-sm">Verified</span>
                </div>
                <p className="text-xs text-white/70 mt-1 font-mono">{certificate.verification_code}</p>
              </div>
            </div>
          </div>

          {/* Body */}
          <div className="px-8 py-10 text-center">
            <p className="text-[#666666] mb-2">This is to certify that</p>
            <h2 className="text-3xl font-bold text-[#191919] mb-3">{certificate.student_name}</h2>
            {certificate.college && (
              <p className="text-[#666666] mb-8">{certificate.college}</p>
            )}

            <p className="text-[#666666] mb-2">has successfully participated in</p>
            <h3 className="text-xl font-bold text-[#0a66c2] mb-6">{certificate.contest_title}</h3>

            {/* Rank Badge */}
            <div className="inline-flex items-center gap-3 px-8 py-4 rounded-xl mb-8" style={{
              backgroundColor: `${rankBadge.color}10`,
              border: `2px solid ${rankBadge.color}30`
            }}>
              <span className="text-4xl">{rankBadge.icon}</span>
              <div className="text-left">
                <p className="text-sm text-[#666666]">Achieved</p>
                <p className="text-2xl font-bold" style={{ color: rankBadge.color }}>{rankBadge.text}</p>
              </div>
            </div>

            <div className="flex justify-center gap-8 text-sm text-[#666666]">
              <div className="flex items-center gap-2">
                <Users size={16} />
                <span>Out of {certificate.total_participants} participants</span>
              </div>
              <div className="flex items-center gap-2">
                <Trophy size={16} />
                <span>Score: {certificate.score}</span>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="bg-[#f3f2ef] px-8 py-4 flex items-center justify-between border-t border-gray-200">
            <div className="flex items-center gap-2 text-[#666666] text-sm">
              <Calendar size={14} />
              <span>Issued: {new Date(certificate.issued_at).toLocaleDateString()}</span>
            </div>
            <div className="flex items-center gap-2 text-[#666666] text-sm">
              <Shield size={14} />
              <span>Verify at: evalentx.com/codearena/verify</span>
            </div>
          </div>
        </div>

        {/* Verification Info */}
        <div className="mt-6 text-center">
          <p className="text-[#666666] text-sm">
            Verify this certificate at{' '}
            <span className="text-[#0a66c2] font-mono">evalentx.com/codearena/verify</span>
            {' '}using code{' '}
            <span className="text-[#915907] font-mono font-semibold">{certificate.verification_code}</span>
          </p>
        </div>
      </div>
    </div>
  );
};

// My Certificates List Page
export const CodeArenaMyCertificatesPage = () => {
  const [certificates, setCertificates] = useState([]);
  const [loading, setLoading] = useState(true);

  const token = localStorage.getItem('codearena_token');

  useEffect(() => {
    fetchMyCertificates();
  }, []);

  const fetchMyCertificates = async () => {
    try {
      const res = await axios.get(`${API}/codearena/me/certificates`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCertificates(res.data.certificates || []);
    } catch (err) {
      console.error('Failed to fetch certificates:', err);
    } finally {
      setLoading(false);
    }
  };

  const getRankBadge = (rank) => {
    if (rank === 1) return { color: '#915907', icon: '🥇' };
    if (rank === 2) return { color: '#666666', icon: '🥈' };
    if (rank === 3) return { color: '#b24020', icon: '🥉' };
    return { color: '#0a66c2', icon: '🏅' };
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#f3f2ef] flex items-center justify-center">
        <Loader className="animate-spin text-[#0a66c2]" size={40} />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f3f2ef]">
      <header className="sticky top-0 z-50 bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-6xl mx-auto px-6 py-4">
          <Link to="/codearena/dashboard" className="flex items-center gap-2 text-[#666666] hover:text-[#191919] transition-colors text-sm">
            <ArrowLeft size={16} />
            Back to Dashboard
          </Link>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-6 py-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-semibold text-[#191919] flex items-center gap-2">
              <Award className="text-[#915907]" size={28} />
              My Certificates
            </h1>
            <p className="text-[#666666] text-sm mt-0.5">{certificates.length} certificates earned</p>
          </div>
        </div>

        {certificates.length > 0 ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {certificates.map((cert, idx) => {
              const rankBadge = getRankBadge(cert.rank);
              return (
                <Link
                  key={idx}
                  to={`/codearena/certificate/${cert.id}`}
                  className="bg-white border border-gray-200 rounded-xl p-5 hover:border-[#915907]/30 hover:shadow-md transition-all group shadow-sm"
                  data-testid={`cert-card-${idx}`}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="w-10 h-10 bg-[#915907]/10 rounded-lg flex items-center justify-center">
                      <Award className="text-[#915907]" size={20} />
                    </div>
                    <span className="text-2xl">{rankBadge.icon}</span>
                  </div>

                  <h3 className="text-base font-semibold text-[#191919] mb-2 group-hover:text-[#915907] transition-colors">
                    {cert.contest_title}
                  </h3>

                  <div className="flex items-center justify-between mb-3">
                    <span className="text-xl font-bold" style={{ color: rankBadge.color }}>
                      #{cert.rank}
                    </span>
                    <span className="text-[#666666] text-sm">
                      of {cert.total_participants}
                    </span>
                  </div>

                  <div className="flex items-center justify-between text-sm text-[#666666]">
                    <span>{new Date(cert.issued_at).toLocaleDateString()}</span>
                    <span className="flex items-center gap-1 text-[#0a66c2] group-hover:underline font-medium">
                      View <ExternalLink size={12} />
                    </span>
                  </div>
                </Link>
              );
            })}
          </div>
        ) : (
          <div className="bg-white border border-gray-200 rounded-xl p-12 text-center shadow-sm">
            <Award className="mx-auto text-gray-300 mb-4" size={64} />
            <h3 className="text-lg font-semibold text-[#191919] mb-2">No Certificates Yet</h3>
            <p className="text-[#666666] text-sm mb-6">Participate in contests to earn certificates!</p>
            <Link
              to="/codearena/contests"
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#0a66c2] text-white rounded-full font-medium hover:bg-[#004182] transition-colors text-sm"
            >
              <Trophy size={16} />
              View Contests
            </Link>
          </div>
        )}
      </div>
    </div>
  );
};

export default CodeArenaCertificatePage;
